// /home/labdoo/zitech-nexus/frontend/src/pages/admin/CampaignsManagement.tsx
import React, { useState, useEffect } from 'react';
import { newsletterAPI } from '../../services/api';
import './CampaignsManagement.css';

// SVG Icons
const Icons = {
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  Send: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  View: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Close: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cm-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16v16H4z" />
      <line x1="8" y1="8" x2="16" y2="8" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="8" y1="16" x2="12" y2="16" />
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
  Users: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
};

interface Campaign {
  id: string;
  subject: string;
  preview_text: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';
  total_recipients: number;
  total_sent: number;
  total_opens: number;
  total_clicks: number;
  scheduled_for: string | null;
  sent_at: string | null;
  created_at: string;
}

const CampaignsManagement: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await newsletterAPI.getCampaigns();
      setCampaigns(response.data);
    } catch (err: any) {
      console.error('Error fetching campaigns:', err);
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async (campaignId: string) => {
    if (!window.confirm('Are you sure you want to send this campaign?')) return;
    
    try {
      await newsletterAPI.sendCampaign(campaignId);
      alert('Campaign sending initiated!');
      fetchCampaigns();
      setShowSendModal(false);
    } catch (err: any) {
      console.error('Error sending campaign:', err);
      alert(err.response?.data?.error || 'Failed to send campaign');
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'draft': return '#6b7280';
      case 'scheduled': return '#f59e0b';
      case 'sending': return '#0ea5e9';
      case 'sent': return '#10b981';
      case 'failed': return '#ef4444';
      case 'cancelled': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusBackground = (status: string) => {
    switch(status) {
      case 'draft': return '#f3f4f6';
      case 'scheduled': return '#fef3c7';
      case 'sending': return '#dbeafe';
      case 'sent': return '#d1fae5';
      case 'failed': return '#fee2e2';
      case 'cancelled': return '#f3f4f6';
      default: return '#f3f4f6';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'draft': return 'Draft';
      case 'scheduled': return 'Scheduled';
      case 'sending': return 'Sending';
      case 'sent': return 'Sent';
      case 'failed': return 'Failed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="cm-loading-container">
        <div className="cm-spinner">
          <Icons.Spinner />
        </div>
        <p className="cm-loading-text">Loading campaigns...</p>
      </div>
    );
  }

  return (
    <div className="cm-container">
      {/* Header */}
      <div className="cm-header">
        <div className="cm-header-left">
          <h1 className="cm-title">Newsletter Campaigns</h1>
          <p className="cm-subtitle">Manage and track your email marketing campaigns</p>
        </div>
        <button 
          className="cm-btn-primary"
          onClick={() => window.location.href = '/dashboard/admin/newsletter/create'}
        >
          <Icons.Plus />
          Create New Campaign
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="cm-error">
          <span className="cm-error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Stats Bar */}
      <div className="cm-stats-bar">
        <div className="cm-stat-item">
          <span className="cm-stat-label">Total Campaigns</span>
          <span className="cm-stat-value">{campaigns.length}</span>
        </div>
        <div className="cm-stat-divider"></div>
        <div className="cm-stat-item">
          <span className="cm-stat-label">Sent</span>
          <span className="cm-stat-value cm-stat-sent">{campaigns.filter(c => c.status === 'sent').length}</span>
        </div>
        <div className="cm-stat-divider"></div>
        <div className="cm-stat-item">
          <span className="cm-stat-label">Scheduled</span>
          <span className="cm-stat-value cm-stat-scheduled">{campaigns.filter(c => c.status === 'scheduled').length}</span>
        </div>
        <div className="cm-stat-divider"></div>
        <div className="cm-stat-item">
          <span className="cm-stat-label">Drafts</span>
          <span className="cm-stat-value cm-stat-draft">{campaigns.filter(c => c.status === 'draft').length}</span>
        </div>
      </div>

      {/* Table */}
      <div className="cm-table-wrapper">
        <table className="cm-table">
          <thead>
            <tr>
              <th className="cm-col-subject">Subject</th>
              <th className="cm-col-status">Status</th>
              <th className="cm-col-recipients">Recipients</th>
              <th className="cm-col-sent">Sent</th>
              <th className="cm-col-opens">Opens</th>
              <th className="cm-col-clicks">Clicks</th>
              <th className="cm-col-created">Created</th>
              <th className="cm-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 ? (
              <tr>
                <td colSpan={8} className="cm-empty-state">
                  <Icons.Empty />
                  <p className="cm-empty-text">No campaigns found</p>
                  <p className="cm-empty-subtext">Create your first campaign to get started</p>
                </td>
              </tr>
            ) : (
              campaigns.map((campaign) => (
                <tr key={campaign.id} className="cm-table-row">
                  <td className="cm-col-subject">
                    <span className="cm-subject">{campaign.subject}</span>
                  </td>
                  <td className="cm-col-status">
                    <span 
                      className="cm-status-badge"
                      style={{
                        background: getStatusBackground(campaign.status),
                        color: getStatusColor(campaign.status)
                      }}
                    >
                      <Icons.StatusDot />
                      {getStatusLabel(campaign.status)}
                    </span>
                  </td>
                  <td className="cm-col-recipients">
                    <span className="cm-recipients">
                      <Icons.Users />
                      {campaign.total_recipients.toLocaleString()}
                    </span>
                  </td>
                  <td className="cm-col-sent">
                    <span className="cm-sent">{campaign.total_sent.toLocaleString()}</span>
                  </td>
                  <td className="cm-col-opens">
                    <span className="cm-opens">{campaign.total_opens.toLocaleString()}</span>
                  </td>
                  <td className="cm-col-clicks">
                    <span className="cm-clicks">{campaign.total_clicks.toLocaleString()}</span>
                  </td>
                  <td className="cm-col-created">
                    <span className="cm-created">
                      <Icons.Calendar />
                      {formatDate(campaign.created_at)}
                    </span>
                  </td>
                  <td className="cm-col-actions">
                    <div className="cm-actions">
                      {campaign.status === 'draft' && (
                        <button 
                          className="cm-action-btn cm-action-send"
                          onClick={() => handleSendCampaign(campaign.id)}
                          title="Send Campaign"
                        >
                          <Icons.Send />
                          Send
                        </button>
                      )}
                      <button 
                        className="cm-action-btn cm-action-view"
                        onClick={() => setSelectedCampaign(campaign)}
                        title="View Details"
                      >
                        <Icons.View />
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Campaign Details Modal */}
      {selectedCampaign && (
        <div className="cm-modal-overlay" onClick={() => setSelectedCampaign(null)}>
          <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cm-modal-header">
              <h2 className="cm-modal-title">Campaign Details</h2>
              <button className="cm-modal-close" onClick={() => setSelectedCampaign(null)}>
                <Icons.Close />
              </button>
            </div>
            <div className="cm-modal-body">
              <div className="cm-detail-group">
                <label className="cm-detail-label">Subject</label>
                <p className="cm-detail-value">{selectedCampaign.subject}</p>
              </div>
              <div className="cm-detail-group">
                <label className="cm-detail-label">Preview Text</label>
                <p className="cm-detail-value">{selectedCampaign.preview_text || 'No preview text'}</p>
              </div>
              <div className="cm-detail-group">
                <label className="cm-detail-label">Status</label>
                <p className="cm-detail-value">
                  <span 
                    className="cm-status-badge"
                    style={{
                      background: getStatusBackground(selectedCampaign.status),
                      color: getStatusColor(selectedCampaign.status)
                    }}
                  >
                    <Icons.StatusDot />
                    {getStatusLabel(selectedCampaign.status)}
                  </span>
                </p>
              </div>
              <div className="cm-detail-group">
                <label className="cm-detail-label">Statistics</label>
                <div className="cm-stats-grid">
                  <div className="cm-stat-box">
                    <span className="cm-stat-box-label">Recipients</span>
                    <span className="cm-stat-box-value">{selectedCampaign.total_recipients}</span>
                  </div>
                  <div className="cm-stat-box">
                    <span className="cm-stat-box-label">Sent</span>
                    <span className="cm-stat-box-value">{selectedCampaign.total_sent}</span>
                  </div>
                  <div className="cm-stat-box">
                    <span className="cm-stat-box-label">Open Rate</span>
                    <span className="cm-stat-box-value">
                      {selectedCampaign.total_sent > 0 
                        ? ((selectedCampaign.total_opens / selectedCampaign.total_sent) * 100).toFixed(1)
                        : 0}%
                    </span>
                  </div>
                  <div className="cm-stat-box">
                    <span className="cm-stat-box-label">Click Rate</span>
                    <span className="cm-stat-box-value">
                      {selectedCampaign.total_sent > 0 
                        ? ((selectedCampaign.total_clicks / selectedCampaign.total_sent) * 100).toFixed(1)
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>
              {selectedCampaign.sent_at && (
                <div className="cm-detail-group">
                  <label className="cm-detail-label">Sent At</label>
                  <p className="cm-detail-value">
                    <Icons.Calendar />
                    {new Date(selectedCampaign.sent_at).toLocaleString()}
                  </p>
                </div>
              )}
              {selectedCampaign.scheduled_for && (
                <div className="cm-detail-group">
                  <label className="cm-detail-label">Scheduled For</label>
                  <p className="cm-detail-value">
                    <Icons.Calendar />
                    {new Date(selectedCampaign.scheduled_for).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
            <div className="cm-modal-footer">
              {selectedCampaign.status === 'draft' && (
                <button 
                  className="cm-modal-btn-send"
                  onClick={() => {
                    setShowSendModal(true);
                    setSelectedCampaign(null);
                  }}
                >
                  <Icons.Send />
                  Send Campaign
                </button>
              )}
              <button 
                className="cm-modal-btn-close"
                onClick={() => setSelectedCampaign(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignsManagement;